import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tabs,
  Tab,
  Grid,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { getApiUrl } from '../config/api';
import { AuthContext } from '../contexts/AuthContext';
import { InternalLayout } from '../Components/InternalLayout';

const GerenciarReservas = () => {
  const { token } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [reservas, setReservas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingReserva, setEditingReserva] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    nome: '',
    tipo: '',
    solicitante: '',
    departamentoSolicitante: '',
    motivoSolicitacao: '',
    dataUso: '',
    horarioReserva: {
      inicio: '',
      fim: ''
    }
  });

  useEffect(() => {
    fetchReservas();
  }, [tabValue, searchTerm, filterTipo]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchReservas = async () => {
    try {
      setLoading(true);
      const categoria = tabValue === 0 ? 'futuras' : 'passadas';
      const params = new URLSearchParams({
        action: 'reservas',
        categoria
      });

      if (searchTerm) params.append('search', searchTerm);
      if (filterTipo) params.append('tipo', filterTipo);

      const response = await fetch(
        getApiUrl(`/api/secretaria/utilidades?${params.toString()}`),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Erro ao carregar reservas');
      }

      const data = await response.json();
      setReservas(data.reservas || []);
    } catch (error) {
      console.error('Erro ao buscar reservas:', error);
      setError('Erro ao carregar reservas');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setError('');

      // Validações
      if (!formData.nome || !formData.tipo || !formData.solicitante || 
          !formData.dataUso || !formData.horarioReserva.inicio || !formData.horarioReserva.fim) {
        setError('Preencha todos os campos obrigatórios');
        return;
      }

      const url = getApiUrl('/api/secretaria/utilidades?action=reservas');
      const method = editingReserva ? 'PUT' : 'POST';
      
      // Preparar payload com data formatada corretamente
      const payload = editingReserva 
        ? { 
            reservaId: editingReserva._id, 
            ...formData,
            // Converter data do input para formato ISO String
            dataUso: new Date(formData.dataUso + 'T00:00:00').toISOString()
          }
        : {
            ...formData,
            // Converter data do input para formato ISO String
            dataUso: new Date(formData.dataUso + 'T00:00:00').toISOString()
          };



      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao salvar reserva');
      }

      const data = await response.json();
      
      if (data.success) {
        handleCloseDialog();
        fetchReservas();
      } else {
        throw new Error(data.message || 'Erro ao salvar reserva');
      }
    } catch (error) {
      console.error('Erro ao salvar reserva:', error);
      setError(error.message);
    }
  };

  const handleDelete = async (reservaId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta reserva?')) {
      return;
    }

    try {
      const response = await fetch(
        getApiUrl('/api/secretaria/utilidades?action=reservas'),
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ reservaId })
        }
      );

      if (!response.ok) {
        throw new Error('Erro ao excluir reserva');
      }

      fetchReservas();
    } catch (error) {
      console.error('Erro ao excluir reserva:', error);
      setError('Erro ao excluir reserva');
    }
  };

  const handleEdit = (reserva) => {
    setEditingReserva(reserva);
    // Converter data para formato local (evitar problemas de timezone)
    const dataLocal = new Date(reserva.dataUso);
    dataLocal.setMinutes(dataLocal.getMinutes() - dataLocal.getTimezoneOffset());
    
    setFormData({
      nome: reserva.nome,
      tipo: reserva.tipo,
      solicitante: reserva.solicitante,
      departamentoSolicitante: reserva.departamentoSolicitante || '',
      motivoSolicitacao: reserva.motivoSolicitacao || '',
      dataUso: dataLocal.toISOString().split('T')[0],
      horarioReserva: {
        inicio: reserva.horarioReserva.inicio,
        fim: reserva.horarioReserva.fim
      }
    });
    setOpenDialog(true);
  };

  const handleOpenDialog = () => {
    setEditingReserva(null);
    setFormData({
      nome: '',
      tipo: '',
      solicitante: '',
      departamentoSolicitante: '',
      motivoSolicitacao: '',
      dataUso: '',
      horarioReserva: {
        inicio: '',
        fim: ''
      }
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingReserva(null);
    setError('');
  };

  const formatData = (dataString) => {
    const data = new Date(dataString);
    // Não aplicar compensação - a data já vem correta do banco
    return data.toLocaleDateString('pt-BR');
  };

  const getTipoColor = (tipo) => {
    return tipo === 'material' ? 'primary' : 'secondary';
  };

  const getTipoIcon = (tipo) => {
    return tipo === 'material' ? <BusinessIcon /> : <EventIcon />;
  };

  const isReservaPassada = (dataUso) => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    
    const dataReserva = new Date(dataUso);
    dataReserva.setHours(0, 0, 0, 0);
    
    return dataReserva < hoje;
  };

  const reservasFiltradas = reservas.filter(reserva => {
    const matchesSearch = !searchTerm || 
      reserva.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reserva.solicitante.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (reserva.departamentoSolicitante || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTipo = !filterTipo || reserva.tipo === filterTipo;
    
    return matchesSearch && matchesTipo;
  });

  const ReservaCard = ({ reserva }) => (
    <Card sx={{ mb: 2, opacity: isReservaPassada(reserva.dataUso) ? 0.7 : 1 }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
              {reserva.nome}
            </Typography>
            
            <Box display="flex" gap={1} mb={2} flexWrap="wrap">
              <Chip 
                icon={getTipoIcon(reserva.tipo)}
                label={reserva.tipo === 'material' ? 'Material' : 'Local'}
                color={getTipoColor(reserva.tipo)}
                size="small"
              />
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <PersonIcon fontSize="small" color="action" />
                  <Typography variant="body2">{reserva.solicitante}</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <EventIcon fontSize="small" color="action" />
                  <Typography variant="body2">{formatData(reserva.dataUso)}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <ScheduleIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    {reserva.horarioReserva.inicio} - {reserva.horarioReserva.fim}
                  </Typography>
                </Box>
                {reserva.departamentoSolicitante && (
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <BusinessIcon fontSize="small" color="action" />
                    <Typography variant="body2">{reserva.departamentoSolicitante}</Typography>
                  </Box>
                )}
              </Grid>
            </Grid>

            {reserva.motivoSolicitacao && (
              <Typography variant="body2" color="text.secondary" mt={2}>
                <strong>Motivo:</strong> {reserva.motivoSolicitacao}
              </Typography>
            )}
          </Box>

          <Box display="flex" flexDirection={isMobile ? "column" : "row"} gap={1} ml={2}>
            <IconButton onClick={() => handleEdit(reserva)} color="primary" size="small">
              <EditIcon />
            </IconButton>
            <IconButton onClick={() => handleDelete(reserva._id)} color="error" size="small">
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <InternalLayout>
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: "1200px", mx: "auto" }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
            mb: 3,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontSize: { xs: "1.5rem", md: "2.125rem" },
            }}
          >
            <EventIcon sx={{ fontSize: { xs: 24, md: 32 } }} />
            Gerenciar Reservas
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
            size={isMobile ? "medium" : "large"}
          >
            {isMobile ? "Nova" : "Nova Reserva"}
          </Button>
        </Box>

        {/* Filtros */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} md={7}>
              <TextField
                fullWidth
                label="Buscar por nome, solicitante ou departamento"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && fetchReservas()}
                sx={{ minHeight: 56 }}
                InputProps={{
                  startAdornment: (
                    <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                      <PersonIcon fontSize="small" color="action" />
                    </Box>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="filtro-tipo-label" shrink={true}>Filtrar por Tipo</InputLabel>
                <Select
                  labelId="filtro-tipo-label"
                  value={filterTipo}
                  label="Filtrar por Tipo"
                  onChange={(e) => setFilterTipo(e.target.value)}
                  displayEmpty
                  notched={true}
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <Box display="flex" alignItems="center" gap={1}>
                          <BusinessIcon fontSize="small" color="action" />
                          <Typography color="text.secondary">Todos os tipos</Typography>
                        </Box>
                      );
                    }
                    
                    return (
                      <Box display="flex" alignItems="center" gap={1}>
                        {selected === 'material' ? (
                          <>
                            <BusinessIcon fontSize="small" color="primary" />
                            <Typography>Material</Typography>
                          </>
                        ) : (
                          <>
                            <EventIcon fontSize="small" color="secondary" />
                            <Typography>Local</Typography>
                          </>
                        )}
                      </Box>
                    );
                  }}
                  sx={{ 
                    minHeight: 56,
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      paddingTop: '16px'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      legend: {
                        maxWidth: 1000
                      }
                    }
                  }}
                >
                  <MenuItem value="">
                    <Box display="flex" alignItems="center" gap={1}>
                      <BusinessIcon fontSize="small" />
                      <Typography>Todos os tipos</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="material">
                    <Box display="flex" alignItems="center" gap={1}>
                      <BusinessIcon fontSize="small" color="primary" />
                      <Typography>Material</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="local">
                    <Box display="flex" alignItems="center" gap={1}>
                      <EventIcon fontSize="small" color="secondary" />
                      <Typography>Local</Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                onClick={() => {
                  setSearchTerm('');
                  setFilterTipo('');
                }}
                sx={{ 
                  height: 56,
                  width: '100%',
                  minWidth: { xs: '100%', md: 'auto' }
                }}
                disabled={!searchTerm && !filterTipo}
              >
                Limpar
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Tabs */}
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
          <Tab label={`Futuras (${tabValue === 0 ? reservasFiltradas.length : '...'})`} />
          <Tab label={`Passadas (${tabValue === 1 ? reservasFiltradas.length : '...'})`} />
        </Tabs>

        {/* Lista de Reservas */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : reservasFiltradas.length === 0 ? (
          <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center" 
            minHeight="200px"
            sx={{ 
              backgroundColor: theme.palette.grey[50],
              borderRadius: 2,
              p: 4,
              textAlign: 'center'
            }}
          >
            <EventIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Nenhuma reserva encontrada
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {tabValue === 0 ? 'Não há reservas futuras.' : 'Não há reservas passadas.'}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
              sx={{ mt: 2 }}
            >
              Criar Primeira Reserva
            </Button>
          </Box>
        ) : (
          <>
            {isMobile ? (
              // Cards para mobile
              <Box>
                {reservasFiltradas.map((reserva) => (
                  <ReservaCard key={reserva._id} reserva={reserva} />
                ))}
              </Box>
            ) : (
              // Tabela para desktop
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Nome</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Solicitante</TableCell>
                      <TableCell>Departamento</TableCell>
                      <TableCell>Data</TableCell>
                      <TableCell>Horário</TableCell>
                      <TableCell align="center">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reservasFiltradas.map((reserva) => (
                      <TableRow 
                        key={reserva._id}
                        sx={{ opacity: isReservaPassada(reserva.dataUso) ? 0.7 : 1 }}
                      >
                        <TableCell>{reserva.nome}</TableCell>
                        <TableCell>
                          <Chip 
                            icon={getTipoIcon(reserva.tipo)}
                            label={reserva.tipo === 'material' ? 'Material' : 'Local'}
                            color={getTipoColor(reserva.tipo)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{reserva.solicitante}</TableCell>
                        <TableCell>{reserva.departamentoSolicitante || '-'}</TableCell>
                        <TableCell>{formatData(reserva.dataUso)}</TableCell>
                        <TableCell>
                          {reserva.horarioReserva.inicio} - {reserva.horarioReserva.fim}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton onClick={() => handleEdit(reserva)} color="primary">
                            <EditIcon />
                          </IconButton>
                          <IconButton onClick={() => handleDelete(reserva._id)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}

        {/* Dialog para Criar/Editar */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingReserva ? 'Editar Reserva' : 'Nova Reserva'}
          </DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              <TextField
                fullWidth
                label="Nome da Reserva *"
                value={formData.nome}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
              />
              
              <FormControl fullWidth>
                <InputLabel>Tipo *</InputLabel>
                <Select
                  value={formData.tipo}
                  label="Tipo *"
                  onChange={(e) => setFormData({...formData, tipo: e.target.value})}
                >
                  <MenuItem value="material">Material</MenuItem>
                  <MenuItem value="local">Local</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                label="Solicitante *"
                value={formData.solicitante}
                onChange={(e) => setFormData({...formData, solicitante: e.target.value})}
              />
              
              <TextField
                fullWidth
                label="Departamento Solicitante"
                value={formData.departamentoSolicitante}
                onChange={(e) => setFormData({...formData, departamentoSolicitante: e.target.value})}
              />

              <TextField
                fullWidth
                label="Motivo da Solicitação"
                multiline
                rows={3}
                value={formData.motivoSolicitacao}
                onChange={(e) => setFormData({...formData, motivoSolicitacao: e.target.value})}
              />

              <TextField
                fullWidth
                label="Data de Uso *"
                type="date"
                value={formData.dataUso}
                onChange={(e) => setFormData({...formData, dataUso: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Horário Início *"
                  type="time"
                  value={formData.horarioReserva.inicio}
                  onChange={(e) => setFormData({
                    ...formData, 
                    horarioReserva: {
                      ...formData.horarioReserva,
                      inicio: e.target.value
                    }
                  })}
                  InputLabelProps={{ shrink: true }}
                />
                
                <TextField
                  fullWidth
                  label="Horário Fim *"
                  type="time"
                  value={formData.horarioReserva.fim}
                  onChange={(e) => setFormData({
                    ...formData, 
                    horarioReserva: {
                      ...formData.horarioReserva,
                      fim: e.target.value
                    }
                  })}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button onClick={handleCloseDialog} variant="outlined" size="large">
              Cancelar
            </Button>
            <Button onClick={handleSubmit} variant="contained" size="large">
              {editingReserva ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </InternalLayout>
  );
};

export default GerenciarReservas;